<template>
    <div class="weather-location-container">
        <div class="weather-display">
            <div class="weather-row">
                <!-- Temperature -->
                <div class="weather-item">
                    <div class="weather-icon-container">
                        <div class="weather-icon-background">
                            <v-icon class="weather-icon">mdi-thermometer</v-icon>
                        </div>
                        <div class="weather-text-container">
                            <p class="weather-text">{{ weatherTemp }}°C</p>
                            <p class="weather-desc">{{ $t('landing-page.clouds') }}</p>
                        </div>
                    </div>
                </div>
                <!-- Humidity -->
                <div class="weather-item mr-5">
                    <div class="weather-icon-container">
                        <div class="weather-icon-background">
                            <v-icon class="weather-icon">mdi-water-percent</v-icon>
                        </div>
                        <div class="weather-text-container">
                            <p class="weather-text">{{ weatherHumidity }}%</p>
                            <p class="weather-desc">{{ $t('landing-page.humidity') }}</p>
                        </div>
                    </div>
                </div>
            </div>
            <div class="weather-row">
                <!-- Wind Speed -->
                <div class="weather-item">
                    <div class="weather-icon-container">
                        <div class="weather-icon-background">
                            <v-icon class="weather-icon">mdi-weather-windy</v-icon>
                        </div>
                        <div class="weather-text-container">
                            <p class="weather-text">{{ weatherWind }} m/s</p>
                            <p class="weather-desc">{{ $t('landing-page.wind') }}</p>
                        </div>
                    </div>
                </div>
                <!-- Rain Chance -->
                <div class="weather-item">
                    <div class="weather-icon-container">
                        <div class="weather-icon-background">
                            <v-icon class="weather-icon">mdi-weather-pouring</v-icon>
                        </div>
                        <div class="weather-text-container">
                            <p class="weather-text">{{ weatherRain }}%</p>
                            <p class="weather-desc">{{ $t('landing-page.rain') }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="vertical-line"></div>
        <div class="location-time">
            <p class="location-text">{{ $t('landing-page.location') }}</p>
            <p class="location">{{ city }}</p>
            <p class="time">{{ currentTime }}</p>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';

export default defineComponent({
    name: 'WeatherLocation',
    props: {
        weatherTemp: {
            type: Number,
            required: true,
        },
        weatherHumidity: {
            type: Number,
            required: true,
        },
        weatherWind: {
            type: Number,
            required: true,
        },
        weatherRain: {
            type: Number,
            required: true,
        },
        currentTime: {
            type: String,
            required: true,
        },
        city: {
            type: String,
            required: true,
        },
    },
});
</script>

<style scoped>
.weather-location-container {
    display: flex;
    align-items: center;
    background-color: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(8px);
    padding: 20px;
    border-radius: 12px;
    width: 38rem;
}

.weather-display {
    display: flex;
    flex-direction: column;
    flex: 1;
}

.weather-row {
    display: flex;
    justify-content: space-around;
    margin-top: 20px;
    margin-bottom: 20px;
}

.weather-item {
    display: flex;
    align-items: center;
}

.weather-icon-container {
    display: flex;
    align-items: center;
    gap: 10px;
}

.weather-icon-background {
    width: 50px;
    height: 50px;
    background-color: rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(8px);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.weather-icon {
    font-size: 24px;
    color: white;
}

.weather-text-container {
    display: flex;
    flex-direction: column;
}

.weather-text {
    font-size: 26px;
    font-weight: bold;
    color: white;
    margin: 5px 0;
}

.weather-desc {
    font-size: 14px;
    font-weight: bold;
    color: white;
    opacity: 0.7;
}

.vertical-line {
    width: 1.2px;
    background-color: rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(8px);
    height: 60%;
    margin: 0 18px;
    position: fixed;
    right: 22%;
}

.location-time {
    display: flex;
    flex-direction: column;
    align-items: center;
    color: white;
}

.location-text,
.location,
.time {
    margin-bottom: 5px;
}

.location-text {
    font-size: 20px;
    font-weight: bold;
}

.location {
    font-size: 18px;
}

.time {
    font-size: 30px;
    font-weight: bold;
}

/* RESPONSIVE DESIGN */
@media (max-width: 1200px) {
    .weather-location-container {
        width: 31rem;
    }

    .weather-text {
        font-size: 22px;
    }

    .weather-desc {
        font-size: 12px;
    }

    .weather-icon-background {
        width: 42px;
        height: 42px;
    }

    .weather-icon {
        font-size: 20px;
    }

    .location-text {
        font-size: 18px;
    }

    .location {
        font-size: 16px;
    }

    .time {
        font-size: 26px;
    }

    .vertical-line {
        left: 69%;
    }
}

@media (max-width: 992px) {
    .weather-location-container {
        flex-direction: row;
        padding: 15px;
        gap: 10px;
    }

    .weather-row {
        flex-direction: row;
        gap: 15px;
    }

    .weather-text {
        font-size: 20px;
    }

    .weather-desc {
        font-size: 11px;
    }

    .weather-icon-background {
        width: 36px;
        height: 36px;
    }

    .weather-icon {
        font-size: 18px;
    }

    .location-text {
        font-size: 16px;
    }

    .location {
        font-size: 14px;
    }

    .time {
        font-size: 24px;
    }

    .vertical-line {
        left: 70%;
        height: 60%;
    }
}

@media (max-width: 770px) {
    .weather-location-container {
        flex-direction: column;
        padding: 10px;
    }

    .weather-row {
        flex-direction: row;
        gap: 15px;
    }

    .weather-text {
        font-size: 16px;
    }

    .weather-desc {
        font-size: 10px;
    }

    .weather-icon-background {
        width: 32px;
        height: 32px;
    }

    .weather-icon {
        font-size: 16px;
    }

    .location-text {
        font-size: 14px;
    }

    .location {
        font-size: 13px;
    }

    .time {
        font-size: 20px;
    }

    .vertical-line {
        display: none;
    }

    .location-time {
        order: -1;
    }
}
</style>
